import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import ChevronRight from '@/assets/icons/chevronRightNew';
import WalletCashIcon from '@/assets/icons/wallet-cash';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { AccountLink } from '@/feature/account-details/components/AccountLink';
import { productNames } from '@/hooks/use-active-product-name';
import { useActiveWallet, useSwitchActiveWallet } from '@/hooks/use-active-wallet';
import { Product } from '@/hooks/use-jurisdiction';
import { AccountTabNavigationProp } from '@/navigation/types';
import { toLocaleCurrency, toSocialCurrency } from '@/utils/numeric/currency';
import { toFraction } from '@/utils/numeric/fraction';

const SHARED_WALLET_PRODUCTS = [
    [Product.Pickem, Product.Arcade, Product.Prediction, Product.Rips],
    [Product.Sweeps, Product.Casino],
];

type SecondaryWalletButtonProps = {
    activeProduct: Product;
};

export const SecondaryWalletButton = ({ activeProduct }: SecondaryWalletButtonProps) => {
    const { navigate } = useNavigation<AccountTabNavigationProp>();
    const { t } = useTranslation(['account', 'wallet']);
    const switchActiveWallet = useSwitchActiveWallet();
    const { wallets } = useActiveWallet();

    const openSecondaryWallet = useCallback(
        (product: Product) => {
            switchActiveWallet(product);
            navigate('SecondaryWallet', { product });
        },
        [navigate, switchActiveWallet]
    );

    // Only show one product per shared wallet, with balance over 0 and not sharing the active product's wallet
    const secondaryWallets = useMemo(() => {
        return SHARED_WALLET_PRODUCTS.filter(group => !group.includes(activeProduct))
            .map(([walletProduct]) => walletProduct)
            .filter(product => {
                const wallet = wallets[product];

                return Object.values(wallet).some(currencyWallet => {
                    // Won't show sweeps wallet if has coins only
                    if (product === Product.Sweeps && currencyWallet === wallet.COINS) {
                        return false;
                    }
                    return Number(currencyWallet?.real_amount || '0') + Number(currencyWallet?.bonus_amount || '0') > 0;
                });
            })
            .map(product => ({ product, wallet: wallets[product] }));
    }, [wallets, activeProduct]);

    if (secondaryWallets.length === 0) {
        return null;
    }

    return (
        <AccountLink>
            {secondaryWallets.map(({ product, wallet }, index) => {
                const defaultCurrency = product === Product.Sweeps ? 'CASH' : 'USD';
                const walletData = wallet[defaultCurrency];
                const balance = walletData ? toFraction(walletData.total) : '0';
                const productName = productNames[product];
                const isLast = index === secondaryWallets.length - 1;

                return (
                    <Pressable key={product} onPress={() => openSecondaryWallet(product)}>
                        <Box
                            paddingVertical={'s16'}
                            flexDirection={'row'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            borderBottomWidth={isLast ? 0 : 1}
                            borderBottomColor={isLast ? undefined : ds.colorToken.border.strong}
                        >
                            <Text variant={'bodyLarge'} color={ds.colorToken.text.primary}>
                                {product === Product.Sweeps
                                    ? t('wallet:social_wallet')
                                    : t('product_wallet', { product: productName })}
                            </Text>
                            <Box flexDirection={'row'} alignItems={'center'}>
                                {product === Product.Sweeps ? (
                                    <Row alignItems={'center'} gap={'s4'}>
                                        <WalletCashIcon size={16} />
                                        <Text variant={'bodyMedium'} color="neutral300">
                                            {toSocialCurrency(balance)}
                                        </Text>
                                    </Row>
                                ) : (
                                    <Text variant={'bodyMedium'} color="neutral300">
                                        {toLocaleCurrency(balance)}
                                    </Text>
                                )}
                                <ChevronRight style={styles.rightIcon} />
                            </Box>
                        </Box>
                    </Pressable>
                );
            })}
        </AccountLink>
    );
};

const styles = StyleSheet.create({
    rightIcon: {
        marginLeft: 12,
    },
});
